import { EventTarget } from "event-target-shim";
import configs from "./configs";
import { getAuthHeaders } from "./getToken";
import { setDashboardBridgeToken } from "./dashboard-bridge";
import { createDpopProof } from "./dpop";
import { getSignedUrl } from "./artwork-utils";
import { uploadBugReportScreenshot } from "./media-utils";
import { hasCanonicalPermission } from "./authorization";
import { logRtcSfuRecovery, summarizeRtcSfuError } from "./rtc-sfu-recovery-debug";
import { createPlatformRoomApi, runtimeRoomToHub } from "./platform-room-api";

export class DashboardApiError extends Error {
  constructor({ path, status, statusText, payload, retryAfter }) {
    super(`Dashboard API request failed: ${status} ${statusText}`);
    this.name = "DashboardApiError";
    this.path = path;
    this.status = status;
    this.statusText = statusText;
    this.payload = payload;
    this.retryAfter = retryAfter;
  }
}

export function classifyDeviceLinkExchangeError(error) {
  if (!(error instanceof DashboardApiError)) {
    return { retryable: false, reason: "invalid_or_expired" };
  }

  const status = error.status;
  const outcome = error.payload?.status;
  if (
    (status === 409 && (outcome === "pending_source_confirmation" || outcome === "issuance_in_progress")) ||
    (status === 503 && outcome === "issuance_unavailable")
  ) {
    return { retryable: true, reason: outcome, retryAfter: error.retryAfter };
  }

  return { retryable: false, reason: "invalid_or_expired" };
}

export function deviceLinkExchangeRetryDelay(attempt, retryAfter) {
  if (Number.isFinite(retryAfter)) {
    return Math.max(0, Math.min(5000, retryAfter * 1000));
  }

  return Math.min(5000, 1500 + Math.max(0, attempt - 1) * 250);
}

function parseRetryAfter(value) {
  if (!value) {
    return null;
  }

  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return seconds;
  }

  const retryAt = Date.parse(value);
  if (!Number.isNaN(retryAt)) {
    return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
  }

  return null;
}

async function readResponsePayload(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (_) {
    return { message: text };
  }
}

function isLocalTenantHost(hostname = window.location.hostname) {
  return /^(localhost|127\.0\.0\.1|0\.0\.0\.0|hubs\.local)$/i.test(hostname);
}

function getTenantHostHeader() {
  if (typeof window === "undefined" || !window.location || isLocalTenantHost()) {
    return {};
  }

  return { "X-Tenant-Host": window.location.host.toLowerCase() };
}

function sourceSecretHeaders(sourceSecret) {
  return sourceSecret ? { "X-Device-Link-Source-Secret": sourceSecret } : {};
}

export default class DashboardChannel extends EventTarget {
  constructor(store, hubId) {
    super();
    this.store = store;
    this.hubId = hubId;
    this.dashboard_api_url_v2 = configs.CUSTOM_API_URL_V2;
    this.hub = null;
    this.platformRoomApi = createPlatformRoomApi({
      request: (...args) => this.request(...args),
      tenantHeaders: getTenantHostHeader
    });
  }

  loadPlatformRuntimeHub = async (hub, { syncSettings = true } = {}) => {
    const id = hub?.hub_id || window?.APP?.hub?.hub_id || this.hubId;
    if (!id) {
      throw new Error("A runtime Room id is required to load Nest authority.");
    }

    const runtimeRoom = await this.platformRoomApi.getRuntimeRoom(id);
    const liveHub = this.applyPlatformRuntimeHub(runtimeRoom, hub);
    if (syncSettings && window?.APP?.hub) {
      this.syncHubSettings(liveHub);
    }

    return liveHub;
  };

  getHub = async () => {
    try {
      if (
        window.hub &&
        window.hub.id === this.hubId &&
        window.hub.room_id &&
        Object.hasOwn(window.hub, "owner_profile")
      ) {
        return window.hub;
      }

      return await this.loadPlatformRuntimeHub(window?.APP?.hub);
    } catch (e) {
      return null;
    }
  };

  applyPlatformRuntimeHub = (runtimeRoom, hub = window?.APP?.hub) => {
    const platformHub = runtimeRoomToHub(runtimeRoom);
    const liveHub = hub || window?.APP?.hub || platformHub;
    Object.assign(liveHub, platformHub);
    if (window?.APP?.hub && window.APP.hub !== liveHub) {
      Object.assign(window.APP.hub, platformHub);
    }

    this.hub = window.hub = liveHub;
    return liveHub;
  };

  syncHubSettings = hub => {
    const hubChannel = APP.hubChannel;

    if (hubChannel) {
      // Merge hub.user_data with the current APP.hub.user_data
      const externalUserData = hub.user_data;
      const currentUserData = APP.hub.user_data;

      // if(APP.hub.name !== hub.name) {
      //   const { description, room_size, user_data, entry_mode, member_permissions } = APP.hub;
      //   const settings = {
      //     name: hub.name,
      //     description,
      //     room_size,
      //     user_data,
      //     entry_mode,
      //     member_permissions
      //   }
      //   hubChannel.updateHub(settings);
      //   APP.hub.name = hub.name;
      // }

      if (externalUserData) {
        if (currentUserData) {
          hub.user_data = {
            ...currentUserData,
            ...externalUserData
          };

          // Check if every value is the same
          if (!Object.keys(currentUserData).every(key => currentUserData[key] === externalUserData[key])) {
            hubChannel.updateHub(hub);
          }
        } else {
          hub.user_data = externalUserData;
          hubChannel.updateHub(hub);
        }
      }
    }
  };

  updateHub = async settings => {
    const runtimeRoom = await this.platformRoomApi.updateRuntimeRoom(this.hubId, settings);
    return runtimeRoom ? this.applyPlatformRuntimeHub(runtimeRoom) : null;
  };

  updateHubDeployment = async selection => {
    const hub = await this.getHub();
    return this.platformRoomApi.changeRoomDeployment(hub, selection);
  };

  updateHubRoom = async settings => {
    const runtimeRoom = await this.platformRoomApi.updateRuntimeRoom(this.hubId, settings);
    return runtimeRoom ? this.applyPlatformRuntimeHub(runtimeRoom) : null;
  };

  shareHub = async (runtimeRoomId = this.hubId) => {
    if (!runtimeRoomId) {
      return null;
    }
    return this.platformRoomApi.shareRuntimeRoom(runtimeRoomId);
  };

  createRoomFeedback = async ({
    roomId = this.hubId,
    overall,
    label,
    anonymousId = this.store?.state?.profile?.id,
    exitReason = "left"
  } = {}) => {
    const feedbackLabel = label === "liked" || label === "like" || overall >= 0 ? "like" : "dislike";

    return this.request(
      `/runtime/rooms/${roomId}/feedback`,
      "POST",
      {
        anonymous_id: anonymousId,
        rating_value: overall >= 0 ? 1 : -1,
        rating_scale: "sentiment",
        label: feedbackLabel,
        source: "metaverse_exit_screen",
        exit_reason: exitReason
      },
      { throwOnError: true }
    );
  };

  requestRtcSfuRoomSession = async ({
    roomId = this.hubId,
    clientId,
    reserve = true,
    roomSessionId,
    reconnectToken
  } = {}) => {
    logRtcSfuRecovery("dashboard-room-session:request", {
      roomId,
      clientId,
      reserve,
      roomSessionId,
      hasReconnectToken: Boolean(reconnectToken)
    });

    try {
      const input = { clientId, reserve, roomSessionId, reconnectToken };
      const session = await this.platformRoomApi.requestRuntimeSession(roomId, input);

      logRtcSfuRecovery("dashboard-room-session:response", {
        roomId,
        state: session?.state,
        canEnter: session?.canEnter,
        peerMode: session?.peerMode,
        roomSessionId: session?.roomSessionId,
        hasAllocationGrant: Boolean(session?.allocationGrant),
        hasReconnectToken: Boolean(session?.reconnectToken)
      });

      return session;
    } catch (err) {
      logRtcSfuRecovery("dashboard-room-session:error", {
        roomId,
        error: summarizeRtcSfuError(err)
      });
      throw err;
    }
  };

  createDeviceLink = async ({ roomPath }) => {
    return this.request("/device-links", "POST", {
      client: "metaverse",
      room_path: roomPath
    });
  };

  getDeviceLink = async (grantId, { sourceSecret } = {}) => {
    return this.request(`/device-links/${grantId}`, "GET", undefined, {
      headers: sourceSecretHeaders(sourceSecret)
    });
  };

  confirmDeviceLink = async ({ grantId, claimId, encryptedHandoffPayload, payloadFormatVersion = 1, sourceSecret }) => {
    return this.request(
      `/device-links/${grantId}/confirm`,
      "POST",
      {
        claim_id: claimId,
        encrypted_handoff_payload: encryptedHandoffPayload,
        payload_format_version: payloadFormatVersion
      },
      {
        headers: sourceSecretHeaders(sourceSecret)
      }
    );
  };

  cancelDeviceLink = async (grantId, { sourceSecret } = {}) => {
    return this.request(`/device-links/${grantId}/cancel`, "POST", undefined, {
      headers: sourceSecretHeaders(sourceSecret)
    });
  };

  claimDeviceLink = async ({ userCode, targetEncryptionPublicJwk }) => {
    return this.request(
      "/device-links/claims",
      "POST",
      {
        user_code: userCode,
        client: "metaverse",
        target_encryption_public_jwk: targetEncryptionPublicJwk
      },
      { requireDpopProof: true }
    );
  };

  exchangeDeviceLinkClaim = async ({ claimId, claimSecret }) => {
    const response = await this.request(
      `/device-links/claims/${claimId}/exchange`,
      "POST",
      {
        claim_secret: claimSecret
      },
      { requireDpopProof: true, credentials: "include", throwOnError: true }
    );

    if (response?.access_token) {
      setDashboardBridgeToken(response.access_token, response.expires_at);
    }

    return response;
  };

  async reportBug(bug) {
    const url = configs["CUSTOM_API_URL"] + "/bugs";

    const { screenshot } = bug;

    let screenshotAssetResourceId = null;

    if (screenshot) {
      screenshotAssetResourceId = await uploadBugReportScreenshot(screenshot);
    }

    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({
        subject: bug.subject,
        description: bug.description,
        screenshot_asset_resource_id: screenshotAssetResourceId,
        severity: bug.severity,
        deviceAffected: bug.devices
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(await getAuthHeaders(url, "POST"))
      }
    }).catch(() => {
      return null;
    });

    if (!response || !response.ok) {
      return null;
    }

    const data = await response.json();

    return data;
  }

  async uploadFile({ file, folder }) {
    const signedUrl = await getSignedUrl({ file, folder });

    await fetch(signedUrl.presigned_url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/octet-stream",
        accept: "application/json",
        "x-ms-blob-type": "BlockBlob"
      },
      body: file
    });

    return { url: signedUrl.download_url };
  }

  request = async (
    path,
    method,
    body,
    { throwOnError = false, headers: requestHeaders = {}, requireDpopProof = false, credentials = "same-origin" } = {}
  ) => {
    const dashboard_api_url = configs.CUSTOM_API_URL;

    try {
      const url = `${dashboard_api_url}${path}`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...requestHeaders
      };

      if (requireDpopProof) {
        // Claim and exchange are public issuance requests, even when already signed in.
        // Token-bound proofs contain ath and are rejected by the issuance verifier.
        headers.DPoP = await createDpopProof({ url, method });
      } else {
        Object.assign(headers, await getAuthHeaders(url, method));
      }

      const response = await fetch(url, {
        method,
        credentials,
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = new DashboardApiError({
          path,
          status: response.status,
          statusText: response.statusText,
          payload: await readResponsePayload(response),
          retryAfter: parseRetryAfter(response.headers.get("Retry-After"))
        });

        console.error(error.message);

        if (throwOnError) {
          throw error;
        }

        return null;
      }

      return response.json();
    } catch (e) {
      if (throwOnError) {
        if (e?.name !== "DashboardApiError") {
          console.error(e);
        }

        throw e;
      }

      console.error(e);

      return;
    }
  };

  can(permission) {
    return hasCanonicalPermission(this.store.state.authUser, permission);
  }

  updateCurrentUserProfile = changes => {
    const userId = this.store?.state?.authUser?.id;

    if (userId === null || userId === undefined || userId === "") {
      return Promise.resolve(null);
    }

    return this.request(`/users/${encodeURIComponent(String(userId))}/profile`, "PUT", changes, { throwOnError: true });
  };

  request_v2 = async (path, method, body) => {
    try {
      if (!this.dashboard_api_url_v2) return;

      const url = `${this.dashboard_api_url_v2}${path}`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-metaverse-token": this.store.state.credentials.token
      };

      Object.assign(headers, await getAuthHeaders(url, method));

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        console.error(`Dashboard API request failed: ${response.status} ${response.statusText}`);

        return null;
      }

      return response.json();
    } catch (e) {
      console.error(e);

      return;
    }
  };
}
